import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import cn from 'classnames';
import { useCustomer } from '../../../../contexts/CustomerContext';
import useQueryString from '../../../../hooks/use-query-string';
import useOrderPackage from '../../../../hooks/useOrderPackage';
import useOfferCoupon from '../../../../hooks/useOfferCoupon';
import useCart from '../../../../hooks/useCart';
import usePoses from '../../../../hooks/usePoses';
import useAddons from '../../../../hooks/useAddons';
import QueryKeys from '../../../../utils/QueryKeys';
import { routes } from '../../../../routes';
import { money } from '../../../../utils/offerPricing';
import { formatDeadline } from '../../../../utils/offerDeadline';
import addFreeMagnets, { FREE_MAGNETS_FALLBACK_VALUE } from '../../../../utils/freeMagnets';
import shouldShowFreeMagnets from './freeMagnetsVisibility';

// The two magnet positions on the "Photo Magnets" gift art -- the same slots
// the Gifts page composes onto (Holiday/Builder/HolidayBuilder.module.scss
// .poseOneMagnet / .poseTwoMagnet), so the child looks the same here as there.
const MAGNET_SLOTS = [
  { top: '19%', left: '15%', width: '40%' },
  { top: '31%', left: '57%', width: '40%' },
];

// Poses whose background is baked in (JPG) are not sold on magnets.
const isPng = (pose) => (pose?.extension || '').toLowerCase().includes('png');

/**
 * "Your 2 FREE photo magnets" -- the dedicated magnet-bonus page (issue #5218
 * item 8). Shown once, after the package is built and BEFORE Announcements &
 * Gifts, so the free pair is not lost among the $29.99 gifts. The elementary
 * sibling of seniors/BuyAllPromo.
 *
 * Gate: shouldShowFreeMagnets (grantsFreeMagnets + cart not already carrying
 * the sheet + not all-JPG), re-asked here against a FRESH cart, and skipped in
 * an effect (never during render) so it cannot double-fire under Strict Mode.
 *
 * Accept posts the picked pose to /carts/:id/free_magnets; the server attaches
 * the $0 sheet through the ONE implementation (Cart#sync_free_magnets!). Either
 * button then continues to `next` (the add-ons chain the caller was headed to).
 * Purely additive: the built package is never touched.
 */
export default function FreeMagnets() {
  const { orderPackageId } = useParams();
  const { next } = useQueryString();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { customer } = useCustomer();

  const studentId = customer?.currentStudent?.id;
  const schoolId = customer?.currentSchool?.id;
  const cartId = customer?.cart?.id;

  const { data: orderPackage, isLoading: loadingPackage } = useOrderPackage(orderPackageId);
  const packageId = orderPackage?.packageId;
  const { data: offer, isLoading: loadingOffer } = useOfferCoupon(customer?.currentStudent?.studentId, schoolId, packageId);
  // Fresh on every mount: hasFreeMagnets flips on accept and the app-wide
  // default is refetchOnMount: false.
  const { data: cart, isLoading: loadingCart } = useCart(cartId, { refetchOnMount: 'always' });
  const { data: poses } = usePoses(studentId, packageId);
  const { data: addons } = useAddons(true);

  const [selectedPoseId, setSelectedPoseId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const selectable = useMemo(() => (poses || []).filter((pose) => pose && isPng(pose)), [poses]);
  const allJpg = (poses || []).length > 0 && selectable.length === 0;

  // Default = the favourite: the first pose on the package just built.
  const defaultPoseId = orderPackage?.sheets?.[0]?.poseId || selectable[0]?.id;
  const poseId = selectedPoseId || defaultPoseId;
  const pose = selectable.find((p) => String(p.id) === String(poseId)) || selectable[0];

  // The paid "Photo Magnets" gift supplies the fridge art and the value line.
  const magnetAddon = (addons || []).find((a) => (
    a.cssName === 'magnet' && Number(a.priceWith) > 0 && a.imageUrl && !a.imageUrl.includes('missing')
  ));
  const value = Number(magnetAddon?.priceWith) > 0 ? Number(magnetAddon.priceWith) : FREE_MAGNETS_FALLBACK_VALUE;
  const deadline = formatDeadline(offer?.expiresOn);

  const continueOn = () => history.push(next || routes.confirm(orderPackageId));

  const ready = !loadingPackage && !loadingOffer && !loadingCart && !!poses;
  const show = ready && !!pose && shouldShowFreeMagnets({
    offer, qualifies: cart?.qualifiesForFreeMagnets === true, hasFreeMagnets: !!cart?.hasFreeMagnets, allJpg,
  });
  const shouldSkip = ready && !show;
  useEffect(() => {
    if (shouldSkip) continueOn();
  }, [shouldSkip]);

  if (!ready || shouldSkip) return null;

  const accept = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await addFreeMagnets({ cartId, poseId: pose.id });
      if (!result?.applied) console.warn('Free magnets not applied:', result?.reason); // eslint-disable-line no-console
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CART] });
      continueOn();
    } catch (err) {
      console.error('Free magnets failed:', err); // eslint-disable-line no-console
      setError('Something went wrong adding your magnets. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-5 pb-10 pt-7 text-center">
      <span className="inline-block rounded-full bg-red-600 px-3.5 py-1.5 text-xs font-extrabold uppercase tracking-widest text-white">
        Included with your package
      </span>

      <h1 className="mt-4 text-3xl font-extrabold leading-tight text-gray-900">Your 2 FREE photo magnets</h1>

      <p className="mx-auto mt-3 max-w-xs text-base leading-snug text-gray-600">
        {`a ${money(value)} value — included with your package`}
        {deadline ? ` until ${deadline}` : ''}
      </p>

      {/* The fridge: the paid magnet gift's art with the child on both magnets. */}
      <div className="relative mx-auto mt-6 w-full max-w-sm overflow-hidden rounded-2xl bg-gray-100 shadow-md" data-testid="magnet-fridge">
        {magnetAddon?.imageUrl
          ? <img src={magnetAddon.imageUrl} alt="" className="block w-full" />
          : <div className="aspect-[4/3] w-full bg-gradient-to-b from-gray-100 to-gray-200" />}
        {MAGNET_SLOTS.map((slot) => (
          <div
            key={slot.left}
            className="absolute overflow-hidden rounded-[10%] border-[3px] border-white bg-white shadow-lg"
            style={{
              top: slot.top, left: slot.left, width: slot.width, aspectRatio: '4 / 5',
            }}
          >
            <img src={pose.watermarkUrl || pose.imageUrl} alt="" className="block h-full w-full object-cover" />
          </div>
        ))}
      </div>

      {selectable.length > 1 && (
        <>
          <p className="mt-6 text-sm font-bold text-gray-900">Pick the photo for your magnets</p>
          <div className="mt-2 flex gap-2 overflow-x-auto px-1 pb-2" role="listbox" aria-label="Magnet photo">
            {selectable.map((p) => {
              const selected = String(p.id) === String(pose.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => setSelectedPoseId(p.id)}
                  className={cn(
                    'shrink-0 overflow-hidden rounded-lg border-0 bg-transparent p-0 ring-offset-2 transition',
                    selected ? 'ring-4 ring-shoob-blue' : 'ring-1 ring-gray-200 opacity-80 hover:opacity-100',
                  )}
                >
                  <img src={p.watermarkUrl || p.imageUrl} alt="" className="block h-24 w-[4.8rem] object-cover" />
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className="mx-auto mt-6 max-w-sm">
        {error && <p className="mb-3 text-sm font-semibold text-red-700">{error}</p>}
        <button
          type="button"
          disabled={saving}
          onClick={accept}
          className="block w-full rounded-full border-0 bg-shoob-blue px-6 py-3.5 text-base font-extrabold text-white shadow-md disabled:opacity-60"
        >
          {saving ? 'Adding your magnets…' : 'Add my free magnets & continue'}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={continueOn}
          className="mt-3 block w-full border-0 bg-transparent text-sm font-semibold text-gray-500 hover:text-gray-900 hover:underline"
        >
          No thanks
        </button>
      </div>
    </div>
  );
}
